import React, { useState, useEffect } from "react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addToCart } from "../store/action/cartAction";
import { Carousel } from "react-responsive-carousel";
import { ToastContainer, toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "react-toastify/dist/ReactToastify.css";
import Cart from "../checkout/Cart";
import DOMPurify from "dompurify";
import "./detail.css";
import Loader from "../home/Loader";
import PostHog from "posthog-js";

import PageTitle from "../store/PageTitle";

const serverUrl = process.env.REACT_APP_SERVER_URL;

function ProductDetails({
  isCartOpen,
  closeCart,
  closeCartRef,
  toggleLogin,
  onBackToProducts,
}) {
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const fromPage = location.state?.page || 1;
  const fromCategory = location.state?.category || "All";

  console.log("Returning to page:", fromPage);

  const navigate = useNavigate();

  const handleBackClick = () => {
    onBackToProducts(fromPage, fromCategory); // ⬅ Pass page and category to Home
    navigate("/"); // ⬅ Navigate back to Home
  };

  // Initialize product only if state exists and IDs match
  const [product, setProduct] = useState(() => {
    const stateProduct = location.state?.product;
    return stateProduct && String(stateProduct.id) === id ? stateProduct : null;
  });

  const [isLoading, setIsLoading] = useState(!product);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Skip fetch if product exists and matches ID
    if (product && String(product.id) === id) {
      setIsLoading(false);
      return;
    }

    const fetchProduct = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const endpoints = [
          `${serverUrl}/get-product/${id}`,
          `${serverUrl}/get-highlight/${id}`,
        ];

        // Prioritize endpoint based on navigation state
        if (location.state?.productType === "highlight") {
          endpoints.reverse();
        }

        let productData = null;
        let fetchError = null;

        for (const endpoint of endpoints) {
          try {
            const response = await fetch(endpoint);
            if (response.ok) {
              const data = await response.json();
              productData = data.product || data.highlight;
              break; // Exit loop on success
            } else if (response.status !== 404) {
              throw new Error(`HTTP ${response.status}`);
            }
          } catch (err) {
            fetchError = err;
          }
        }

        if (productData) {
          setProduct(productData);
        } else {
          throw fetchError || new Error("Product not found");
        }
      } catch (error) {
        console.error("Fetch error:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id, location.state, product]);
  // Track page view with PostHog
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleAddToCart = (rawProduct) => {
    try {
      const normalizedProduct = normalizeCartItem(rawProduct);
      dispatch(addToCart(normalizedProduct));
      toast.success("Successfully added to cart 🥳");

      console.log("added", product);
      // Track the "Add to Cart" event
      PostHog.capture("Add to Cart", {
        property: "Product Name",
        value: product.item_name,
      });
      PostHog.capture("Add to Cart", {
        property: "Product Price",
        value: product.price,
      });
    } catch (error) {
      toast.error("Error adding to cart:", error.message);
    }
  };

  // Inside ProductDetails component

  const normalizedProduct = product
    ? {
        id: product.id,
        name: product.item_name || product.title,
        description: product.description,
        price: parseFloat(product.price) || 0,
        images: product.itemPhotos || [product.main_img],
        available: product.available || product.type,
        condition: product.condition || "no info",
        type: product.type || "regular",
      }
    : null;

  // Create a normalization utility function
  const normalizeCartItem = (product) => {
    // If it's already a regular product, return as-is
    if (product.item_name) return product;
    console.log("Normalizing product:", product);

    // Normalize highlight products
    // ...existing code...
    if (product.type === "highlight") {
      return {
        id: product.id,
        item_name: product.title || product.item_name || "Unnamed Product",
        itemPhotos: Array.isArray(product.itemPhotos)
          ? product.itemPhotos.filter(Boolean)
          : product.itemPhotos
          ? [product.itemPhotos]
          : [product.main_img].filter(Boolean),
        price:
          typeof product.price === "string"
            ? parseFloat(product.price)
            : Number(product.price),
        quantity: 1,
        description: product.description || "",
        category: product.category || "highlight",
        features: product.features || [],
        originalType: "highlight",
      };
    }
    // ...existing code...
  };

  // Usage in your add to cart handler
  // const handleAddToCart = (rawProduct) => {
  //   const normalizedProduct = normalizeCartItem(rawProduct);
  //   dispatch(addToCart(normalizedProduct));
  //   toast.success("Successfully added to cart 🥳");
  // };

  if (isLoading) {
    return (
      <div className="product-details-container">
        <div className="product-details-grid">
          {/* Skeleton for Images */}
          <div className="product-images">
            <Skeleton height={300} />
          </div>

          {/* Skeleton for Product Info */}
          <div className="product-info">
            <Skeleton height={30} width={200} />
            <Skeleton height={20} width={100} style={{ marginTop: "10px" }} />
            <Skeleton height={20} width={150} style={{ marginTop: "10px" }} />
            <Skeleton height={50} width={300} style={{ marginTop: "20px" }} />
            <Skeleton height={100} style={{ marginTop: "20px" }} />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return <p>No product information available.</p>;
  }

  const photos = normalizedProduct?.images
    ? Array.isArray(normalizedProduct.images)
      ? normalizedProduct.images
      : [normalizedProduct.images]
    : [];

  return (
    <div className="product-details-container">
      <PageTitle title={normalizedProduct.name} />
      <button
        className="back-btn"
        onClick={handleBackClick}
        style={{
          position: "fixed", // ✅ Changed from absolute to fixed
          top: "90px",
          left: "20px",
          zIndex: 1000,
          background: "#e53935", // Red background
          color: "black", // Black text
          padding: "0.5rem 1.5rem",
          borderRadius: "4px",
          border: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          fontWeight: "bold",
          fontSize: "1rem",
          cursor: "pointer",
        }}
      >
        ← Back
      </button>

      {isCartOpen && (
        <Cart
          closeCart={closeCart}
          toggleLogin={toggleLogin}
          closeCartRef={closeCartRef}
        />
      )}
      <div className="product-details-grid">
        {/* Left Column - Product Images */}

        <div className="product-images">
          <Carousel
            showArrows={true}
            showStatus={false}
            showThumbs={true}
            infiniteLoop={true}
          >
            {photos.map((image, index) => (
              <div key={index}>
                <img
                  src={image}
                  alt={`${normalizedProduct.name} - view ${index + 1}`}
                />
              </div>
            ))}
          </Carousel>
        </div>
        {isLoading && <Loader />}

        {/* Right Column - Product Info */}
        <div className="product-info">
          <div className="product-header">
            <h1 className="product-name">{normalizedProduct.name}</h1>
            <div className="condition-tag">{normalizedProduct.condition}</div>
          </div>

          <div className="product-price">
            {product.discountPrice ? (
              <>
                <span className="original-price">
                  ${normalizedProduct.price}
                </span>
              </>
            ) : (
              <span>K{normalizedProduct.price}</span>
            )}
          </div>

          <div className="product-actions">
            {/* <div className="quantity-selector">
              <label htmlFor="quantity">Quantity:</label>
              <select 
                id="quantity" 
                value={quantity} 
                onChange={handleQuantityChange}
              >
                {[...Array(10).keys()].map(num => (
                  <option key={num + 1} value={num + 1}>
                    {num + 1}
                  </option>
                ))}
              </select>
            </div> */}

            <button
              className="add-to-cart-btn"
              onClick={() => {
                if (normalizedProduct.type === "compact") {
                  handleAddToCart(product);
                } else {
                  handleAddToCart(product);
                }
              }}
              disabled={
                (normalizedProduct.type === "featured" &&
                  !normalizedProduct.available) ||
                (!normalizedProduct.available &&
                  normalizedProduct.type !== "compact")
              }
            >
              {normalizedProduct.type === "compact"
                ? normalizedProduct.available
                  ? normalizedProduct.button_text || "Buy Now"
                  : "Unavailable"
                : normalizedProduct.available
                ? "Add to Cart"
                : "Out of Stock"}
            </button>
          </div>

          <div className="product-description">
            <h2>Description</h2>
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(normalizedProduct.description),
              }}
            />
            {/* <p>{normalizedProduct.description}</p> */}
          </div>
          {/* 
         <div className="technical-info">
            <h2>Technical Information</h2>
            <ul className="specs-list">

                <li key={key}>
                  <span className="spec-label">intel i5 10th</span>
                  <span className="spec-value">2gb dedicataded</span>
                </li>
         
            </ul>
          </div> */}
        </div>
      </div>

      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Similar Products Section */}
      {/* <div className="similar-products">
        <h2>Similar Products</h2>
        <div className="similar-products-grid">
          <SimilarProducts currentProduct={product} />
        </div>
      </div> */}
    </div>
  );
}

export default ProductDetails;
